import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { ListItemIcon } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import HeartBrokenRoundedIcon from "@mui/icons-material/HeartBrokenRounded";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { AccountModel } from "../../../../domain/models";
import { Select } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";

interface Props {
  title?: string;
  onBack?: () => void;
  window?: () => Window;
}

const drawerWidth = 200;

export default function DrawerAppBar(props: Props) {
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [warehouses, setWarehouses] = React.useState<any[]>([]);
  const [currentWarehouse, setCurrentWarehouseState] = React.useState<
    { id: number; name: string } | undefined
  >();

  const getCurrentWarehouse = () => {
    const warehouseString = localStorage.getItem("currentWarehouse");
    const warehouse = warehouseString
      ? (JSON.parse(warehouseString) as {
          id: number;
          name: string;
        } | null)
      : undefined;
    return warehouse;
  };

  const setCurrentWarehouse = (warehouse?: { id: number; name: string }) => {
    if (warehouse) {
      localStorage.setItem("currentWarehouse", JSON.stringify(warehouse));
    } else {
      localStorage.removeItem("currentWarehouse");
    }
  };

  const getCurrentAccount = () => {
    const account = localStorage.getItem("account");
    if (account) return JSON.parse(account);
    return {};
  };

  const setCurrentAccount = (account: AccountModel | undefined) => {
    if (account) {
      localStorage.setItem("account", JSON.stringify(account));
    } else {
      localStorage.removeItem("account");
    }
  };

  React.useEffect(() => {
    if (getCurrentAccount) {
      const account = getCurrentAccount();
      setWarehouses(account.warehouses);
      if (account?.warehouses?.length === 1) {
        setCurrentWarehouseState(account.warehouses[0]);
        return setCurrentWarehouse(account.warehouses[0]);
      }
    }
    if (getCurrentWarehouse) {
      const current = getCurrentWarehouse();
      setCurrentWarehouseState(current || undefined);
    }
  }, []);

  const navigate = useNavigate();

  const navItems = [
    {
      text: "Tarefas",
      icon: <CheckBoxRoundedIcon />,
      onClick: () => {
        navigate("/mobile/tasks");
      },
    },
    {
      text: "Relatar Quebra",
      icon: <HeartBrokenRoundedIcon fontSize="small" />,
      onClick: () => {
        navigate("/mobile/breakage");
      },
    },
    {
      text: "Sair",
      icon: <LogoutIcon fontSize="small" />,
      onClick: () => {
        setCurrentAccount(undefined);
        localStorage.removeItem("currentWarehouse");
        navigate("/login");
      },
    },
    // { text: "Avarias", icon: <HeartBrokenRoundedIcon /> },
    // { text: "Indicadores", icon: <ShowChartRoundedIcon /> },
    // { text: "Notificações", icon: <NotificationsNoneRoundedIcon /> },
    // { text: "Perfil", icon: <AccountBoxOutlinedIcon /> },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const drawer = (
    <>
      <Box onClick={handleDrawerToggle}>
        <List>
          <ListItem>
            <ListItemButton>
              <ListItemIcon>
                <CancelIcon />
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
      <Box onClick={handleDrawerToggle}>
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Select
            value={currentWarehouse?.id || ""}
            label={"Unidade"}
            variant="standard"
            sx={{ color: "inherit", width: "90%" }}
            onChange={(e) => {
              const warehouse = warehouses.find(
                (item) => item.id === e.target.value
              );

              setCurrentWarehouse(warehouse);
              setCurrentWarehouseState(warehouse);
            }}
          >
            {warehouses.map((warehouse) => (
              <MenuItem key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </MenuItem>
            ))}
          </Select>
        </div>
        <List>
          {navItems.map((item) => (
            <ListItem key={item.text}>
              <ListItemButton
                sx={{ textAlign: "center" }}
                onClick={item.onClick}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} sx={{ textAlign: "left" }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "flex-end",
        }}
      >
        <List>
          <ListItem>
            <ListItemButton sx={{ textAlign: "center" }}>
              <ListItemIcon>
                <WarningRoundedIcon />
              </ListItemIcon>
              <ListItemText
                primary={"Relatar Erro"}
                sx={{ textAlign: "left" }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: "flex", height: "90px" }}>
      <CssBaseline />
      <AppBar component="nav" sx={{ borderRadius: "0 0 20px 20px" }}>
        <Toolbar
          sx={{
            justifyContent: "space-between",
            height: "60px",
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            sx={{ mr: 2, display: { sm: "none" } }}
            onClick={props.onBack}
          >
            <ArrowBackIosRoundedIcon />
          </IconButton>
          <Typography variant="h6" sx={{ my: 2, fontSize: "16px" }}>
            {props.title}
          </Typography>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box component="nav">
        <Drawer
          anchor="right"
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </Box>
  );
}
